/**
 * <product-gallery>
 *
 * The product page's image column. Two behaviours over one set of markup:
 *
 * - Below 768px: a Swiper, so the row can be swiped and the next image peeks in
 *   at the right edge.
 * - At 768px and above: the Swiper is destroyed and the thumbnail strip drives
 *   which image is shown, by toggling `data-gallery-active` on the slides.
 *
 * Loaded from sections/product.liquid together with assets/swiper-bundle.min.js.
 * Both are `defer`, so the Swiper global is guaranteed to exist by the time this
 * runs — the guard below covers the vendor file failing to load entirely.
 *
 * WITH A SINGLE IMAGE THIS COMPONENT DOES NOTHING AT ALL. It returns early before
 * touching Swiper, which is what the brief asks for: no swiper when there is
 * nothing to swipe to. The thumbnail strip is not rendered by the snippet in that
 * case either, so there is no dead control left behind.
 *
 * ─── Events in ──────────────────────────────────────────────────────────────
 *
 * Listens on the closest [data-product-section] for 'variant:change', dispatched by
 * assets/product-form.js, and shows the photograph assigned to that variant in the
 * products admin. When `imageId` is null — the merchant assigned no image to that
 * variant — the gallery is deliberately left exactly as it is, rather than jumping
 * back to the first photograph, which would look like a glitch on a product where
 * only some variants have their own image.
 *
 * This listener is attached even when there is only ONE image, unlike everything
 * else here: a single-image product can still have variants, and a no-op is the
 * correct response. The early return below therefore comes after it.
 *
 * Like assets/collection-carousel.js, this NEVER adds or removes the
 * `swiper` / `swiper-wrapper` / `swiper-slide` class names. The desktop layout
 * out-specifies swiper.min.css from the section's own style block instead. That
 * approach was tried the other way round on the collection carousel and caused a
 * worse bug; the note at the top of that file has the detail.
 *
 * Expected markup — see snippets/product-gallery.liquid:
 *
 *   <product-gallery data-gap-mobile="12">
 *     <button data-gallery-thumb data-gallery-index="0" aria-current="true">
 *     <div class="swiper">
 *       <div class="swiper-wrapper product-gallery-track">
 *         <div class="swiper-slide product-gallery-slide"
 *              data-gallery-index="0" data-gallery-active="true">
 */

if (!customElements.get('product-gallery')) {
  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      /* The `md` breakpoint used everywhere else in the design system — the
         @utility blocks in css-tailwind.css, Tailwind's own md: variant, and the
         media query in snippets/product-gallery.liquid. If it ever moves, it moves
         in all of those places. */
      static DESKTOP_BREAKPOINT = 768;

      connectedCallback() {
        this.container = this.querySelector('.swiper');
        this.slides = Array.from(this.querySelectorAll('[data-gallery-index][data-gallery-active]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));

        /* Attached FIRST, before the single-image bail-out below, because a product
           with one photograph can still have variants — and answering that with a
           no-op is correct behaviour, not a reason to skip wiring it up. */
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.section = this.closest('[data-product-section]');
        if (this.section) {
          this.section.addEventListener('variant:change', this.handleVariantChange);
        }

        /* One image, or markup that is not what this component expects: leave the
           rest of the page exactly as the server rendered it. */
        if (!this.container || this.slides.length < 2) {
          return;
        }

        this.handleThumbClick = this.handleThumbClick.bind(this);
        this.handleBreakpoint = this.handleBreakpoint.bind(this);

        this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.handleThumbClick));

        this.desktopQuery = window.matchMedia(`(min-width: ${ProductGallery.DESKTOP_BREAKPOINT}px)`);
        this.desktopQuery.addEventListener('change', this.handleBreakpoint);

        this.handleBreakpoint();
      }

      disconnectedCallback() {
        if (this.section) {
          this.section.removeEventListener('variant:change', this.handleVariantChange);
        }

        this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.handleThumbClick));

        if (this.desktopQuery) {
          this.desktopQuery.removeEventListener('change', this.handleBreakpoint);
        }

        this.destroySwiper();
      }

      /* A data attribute that must parse as a number, or the fallback. */
      number(name, fallback) {
        const value = parseFloat(this.dataset[name]);
        return Number.isFinite(value) ? value : fallback;
      }

      handleBreakpoint() {
        if (this.desktopQuery.matches) {
          this.destroySwiper();

          /* Crossing up from mobile leaves whichever image the customer swiped to
             as the active one, so the desktop view opens on the picture they were
             already looking at rather than snapping back to the first. */
          this.select(this.activeIndex());
        } else {
          this.buildSwiper();
        }
      }

      /* The index currently marked active, defaulting to the first slide. */
      activeIndex() {
        const active = this.slides.findIndex(
          (slide) => slide.dataset.galleryActive === 'true',
        );
        return active === -1 ? 0 : active;
      }

      buildSwiper() {
        if (this.swiper || typeof Swiper === 'undefined') return;

        this.swiper = new Swiper(this.container, {
          /* Widths come from the section's style block. `auto` just tells Swiper
             to measure them. */
          slidesPerView: 'auto',

          /* Snapping ON here, unlike the product and collection carousels. Those
             are browsable rows where a card resting half off-screen is the whole
             look; this is a gallery of one product's photographs, where a customer
             swiping expects to land on the next picture squarely. */
          spaceBetween: this.number('gapMobile', 12),

          grabCursor: true,

          /* forceToAxis so a two-finger trackpad gesture only drives the gallery
             when it is genuinely horizontal — otherwise scrolling past the section
             would hijack the page. */
          mousewheel: { forceToAxis: true },

          a11y: { enabled: true },

          /* Arrow-key control, scoped to while the gallery is on screen. pageUpDown
             is off deliberately: it defaults to true in Swiper and would claim Page
             Up / Page Down, which are how a keyboard user scrolls the page. */
          keyboard: { enabled: true, onlyInViewport: true, pageUpDown: false },

          /* Keeps the thumbnail strip and the active flags in step with a swipe, so
             crossing the breakpoint mid-browse lands on the right image. */
          on: {
            slideChange: (swiper) => this.markActive(swiper.activeIndex),
          },
        });

        /* Opening the mobile view on whatever was selected on desktop, for the same
           reason the desktop branch above restores the swiped-to image. */
        const active = this.activeIndex();
        if (active > 0) {
          this.swiper.slideTo(active, 0);
        }
      }

      destroySwiper() {
        if (!this.swiper) return;

        /* destroy(true, true) also strips the inline transform and sizing Swiper
           wrote onto the wrapper and slides, which is what lets the desktop rules
           in the section's style block take over cleanly. */
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      handleThumbClick(event) {
        const index = parseInt(event.currentTarget.dataset.galleryIndex, 10);
        if (!Number.isFinite(index)) return;

        this.select(index);
      }

      /* A variant was chosen. Show its photograph if the merchant gave it one. */
      handleVariantChange(event) {
        const imageId = event.detail && event.detail.imageId;

        /* null / undefined means no image is assigned to this variant. Leave the
           gallery alone — see the note at the top of this file. */
        if (imageId === null || imageId === undefined) return;

        const index = this.slides.findIndex(
          (slide) => slide.dataset.imageId === String(imageId),
        );

        /* The image could be missing from the gallery if it is not among
           product.images — defensive, but a mismatch should scroll nowhere rather
           than to slide 0. */
        if (index === -1) return;

        this.select(index);
      }

      /* Show one image, mark its thumbnail, and keep Swiper in step.
         The Swiper call is guarded rather than branched on breakpoint: on desktop
         there is no instance, and on mobile there is. */
      select(index) {
        this.markActive(index);

        if (this.swiper) {
          this.swiper.slideTo(index);
        }
      }

      markActive(index) {
        this.slides.forEach((slide, i) => {
          slide.dataset.galleryActive = i === index ? 'true' : 'false';
        });

        /* aria-current rather than a class, so the selected thumbnail is announced
           rather than only outlined — state must never be carried by colour alone. */
        this.thumbs.forEach((thumb, i) => {
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      }
    },
  );
}
